import { useEffect, useState, useRef } from 'react'

interface ProcessingOverlayProps {
  visible: boolean
  steps: string[]
  stepDuration?: number
  onComplete?: () => void
}

export default function ProcessingOverlay({ visible, steps, stepDuration = 1000, onComplete }: ProcessingOverlayProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const completedRef = useRef(false)

  useEffect(() => {
    if (!visible) {
      setCurrentStep(0)
      completedRef.current = false
      return
    }

    let step = 0
    const interval = setInterval(() => {
      step++
      if (step >= steps.length) {
        clearInterval(interval)
        if (!completedRef.current) {
          completedRef.current = true
          setTimeout(() => onCompleteRef.current?.(), 300)
        }
        return
      }
      setCurrentStep(step)
    }, stepDuration)

    return () => clearInterval(interval)
  }, [visible, steps.length, stepDuration])

  if (!visible) return null

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 mx-8 text-center animate-scale-in shadow-xl max-w-[320px] w-full">
        {/* Spinner */}
        <div className="w-16 h-16 mx-auto mb-6 relative">
          <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
          <div className="absolute inset-0 rounded-full border-4 border-yokoso-red border-t-transparent animate-spin" />
        </div>

        {/* Steps */}
        <div className="space-y-2">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`text-sm transition-all duration-300 ${
                i === currentStep
                  ? 'text-navy font-semibold scale-105'
                  : i < currentStep
                    ? 'text-matcha'
                    : 'text-text-disabled'
              }`}
            >
              {i < currentStep ? '✓ ' : i === currentStep ? '● ' : '○ '}
              {step}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
